// src/pages/EndOfSeason.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Panel from "../central/Panel";
import Button from "../central/Button";
import Dropdown from "../central/Dropdown";
import { db } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { paths } from "../lib/paths";
import PageLoader from "../components/PageLoader";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: ${({ theme }) => theme.spacing.xxlarge};
  min-height: calc(100vh - 80px);
`;

const WizardPanel = styled(Panel)`
  width: 100%;
  max-width: 650px;
  padding: ${({ theme }) => theme.spacing.xlarge};
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.large};
`;

const StepHeader = styled.h3`
  margin-top: ${({ theme }) => theme.spacing.large};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  gap: 12px;
`;

const DAY_OPTIONS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => ({
  value: d,
  label: d,
}));

const TIME_OPTIONS = [
  { value: "Daytime", label: "Daytime" },
  { value: "Evening", label: "Evening" },
];

const findOption = (options, raw) => {
  if (!raw) return null;
  const val = typeof raw === "string" ? raw : raw.value;
  return options.find(o => o.value === val) || null;
};

// Helper: get next date for a given day of week (e.g. "Monday"), from today (no +1 shift)
function getNextDateForDay(dayName) {
  const days = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
  const now = new Date();
  const todayIdx = now.getDay();
  const targetIdx = days.indexOf(dayName);
  const todayIdxMon = todayIdx === 0 ? 6 : todayIdx - 1;
  let add = (targetIdx - todayIdxMon + 7) % 7;
  const d = new Date(now);
  d.setDate(now.getDate() + add);
  return d;
}

function computeDeadlineFromDayAndTime(matchDay, timeOfDay) {
  const days = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
  if (!days.includes(matchDay)) return null;
  let date = getNextDateForDay(matchDay);
  if (timeOfDay === "Evening") {
    date.setHours(14,0,0,0);
  } else {
    date.setHours(9,0,0,0);
  }
  return date;
}

const EndOfSeason = () => {
  const { leagueID } = useParams();
  const navigate = useNavigate();

  const [leagueName, setLeagueName] = useState("");
  const [loading, setLoading] = useState(true);

  const [priceChoice, setPriceChoice] = useState("original");
  const [confirming, setConfirming] = useState(false);

  const [matchDay, setMatchDay] = useState(null);
  const [timeOfDay, setTimeOfDay] = useState(null);
  const [chipsEnabled, setChipsEnabled] = useState(false);
  const [computedDeadline, setComputedDeadline] = useState(null);

  const [currentSeasonNum, setCurrentSeasonNum] = useState(1);
  const [nextSeasonNum, setNextSeasonNum] = useState(2);

  useEffect(() => {
    const fetchLeague = async () => {
      try {
        const leagueRef = doc(db, "leagues", leagueID);
        const snap = await getDoc(leagueRef);
        if (snap.exists()) {
          const data = snap.data();
          setLeagueName(data.leagueName || data.name || "League");
          const seasonStr = data.currentSeasonID || "S1";
          const currentSeasonNum = parseInt(seasonStr.replace("S",""),10) || 1;
          const nextSeasonNum = currentSeasonNum + 1;
          setCurrentSeasonNum(currentSeasonNum);
          setNextSeasonNum(nextSeasonNum);
          setPriceChoice("original");

          const rawMatchDay = data.matchDay || "Saturday";
          const rawTimeOfDay = data.timeOfDay || "Daytime";

          const initialMatchDayOpt = findOption(DAY_OPTIONS, rawMatchDay);
          const initialTimeOfDayOpt = findOption(TIME_OPTIONS, rawTimeOfDay);

          setMatchDay(initialMatchDayOpt);
          setTimeOfDay(initialTimeOfDayOpt);
          setChipsEnabled(!!data.chipsEnabled);

          const dayVal = initialMatchDayOpt ? initialMatchDayOpt.value : null;
          const timeVal = initialTimeOfDayOpt ? initialTimeOfDayOpt.value : null;
          const dd = dayVal && timeVal
            ? computeDeadlineFromDayAndTime(dayVal, timeVal)
            : null;

          setComputedDeadline(
            dd
              ? {
                  timestamp: dd,
                  formatted: dd.toLocaleString("en-GB", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                }
              : null
          );
        }
      } catch (err) {
        console.error("Failed to load league:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeague();
  }, [leagueID]);

  useEffect(() => {
    if (!matchDay || !timeOfDay) {
      setComputedDeadline(null);
      return;
    }

    const dayVal = typeof matchDay === "string" ? matchDay : matchDay.value;
    const timeVal = typeof timeOfDay === "string" ? timeOfDay : timeOfDay.value;

    const d = computeDeadlineFromDayAndTime(dayVal, timeVal);
    setComputedDeadline(
      d
        ? {
            timestamp: d,
            formatted: d.toLocaleString("en-GB", {
              weekday: "short",
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            }),
          }
        : null
    );
  }, [matchDay, timeOfDay]);


  const normalizeOption = (raw, options) => {
    if (!raw) return null;
    if (typeof raw === "string") {
      const found = options.find(o => o.value === raw);
      return found ? found : { value: raw, label: raw };
    }
    if (raw.value) {
      return { value: raw.value, label: raw.label || raw.value };
    }
    return null;
  };

  const handleConfirm = () => {
    const md = normalizeOption(matchDay, DAY_OPTIONS);
    const tod = normalizeOption(timeOfDay, TIME_OPTIONS);

    navigate(`/endofseason/${leagueID}/review`, {
      state: {
        priceChoice,
        matchDay: md,
        timeOfDay: tod,
        chipsEnabled,
        computedDeadline
      }
    });
  };

  return (
    <>
      <Container>
        <WizardPanel>
          {loading ? (
            <PageLoader />
          ) : (
            <>
              <Title>End of Season • Season {currentSeasonNum} • {leagueName}</Title>
              <StepHeader>You are about to end Season {currentSeasonNum}...</StepHeader>

              <p>
                1. This will end Season {currentSeasonNum} and create Season {nextSeasonNum}<br />
                2. Your results will be saved<br />
                3. Your players, matches, and scores will reset<br />
                4. Season {nextSeasonNum} settings will be applied as you desire below
              </p>

              <StepHeader>Season Settings</StepHeader>
              <h4>Player Price Rules</h4>

              <OptionRow>
                <input
                  type="radio"
                  name="priceChoice"
                  value="original"
                  checked={priceChoice === "original"}
                  onChange={() => setPriceChoice("original")}
                />
                <label>Reset players to original Season {currentSeasonNum} starting prices</label>
              </OptionRow>

              <OptionRow>
                <input
                  type="radio"
                  name="priceChoice"
                  value="manual"
                  checked={priceChoice === "manual"}
                  onChange={() => setPriceChoice("manual")}
                />
                <label>Set new prices manually</label>
              </OptionRow>

              <OptionRow>
                <input
                  type="radio"
                  name="priceChoice"
                  value="auto"
                  checked={priceChoice === "auto"}
                  onChange={() => setPriceChoice("auto")}
                />
                <label>Auto‑generate prices from Season {currentSeasonNum} performance</label>
              </OptionRow>
              <h/><br />


              <h4>Match Day & Time</h4>

              <p><b>Initial Deadline:</b> {computedDeadline ? computedDeadline.formatted : "Not set"}</p>

              <OptionRow>
                <label style={{ fontWeight: 600, minWidth: "110px" }}>Match Day:</label>
                <Dropdown
                  value={matchDay}
                  onChange={opt => setMatchDay(opt)}
                  options={DAY_OPTIONS}
                  placeholder="Select Match Day"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </OptionRow>

              <OptionRow>
                <label style={{ fontWeight: 600, minWidth: "110px" }}>Match Time:</label>
                <Dropdown
                  value={timeOfDay}
                  onChange={opt => setTimeOfDay(opt)}
                  options={TIME_OPTIONS}
                  placeholder="Select Match Time"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </OptionRow>

              <StepHeader>Chips</StepHeader>

              <OptionRow>
                <input
                  type="radio"
                  id="chipsYes"
                  name="chips"
                  value="yes"
                  checked={chipsEnabled === true}
                  onChange={() => setChipsEnabled(true)}
                />
                <label htmlFor="chipsYes">Enable chips</label>
              </OptionRow>

              <OptionRow>
                <input
                  type="radio"
                  id="chipsNo"
                  name="chips"
                  value="no"
                  checked={chipsEnabled === false}
                  onChange={() => setChipsEnabled(false)}
                />
                <label htmlFor="chipsNo">Disable chips</label>
              </OptionRow>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
                <Button variant="blue" onClick={() => navigate(`/admin/${leagueID}`)}>
                  Back to Manage
                </Button>
                <Button variant="pink" onClick={handleConfirm}>
                  Continue
                </Button>
              </div>
            </>
          )}
        </WizardPanel>
      </Container>
    </>
  );
};

export default EndOfSeason;